import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../stores/store";
import { setCurrentSpecimen } from "../../../stores/specimens";
import {
  fetchSpecimenImagesById,
  fetchSpecimensByCollectionId,
  updateSpecimen,
} from "../../../stores/specimens/actions";
import { useWebServer } from "../../../providers";
import { Grid, Box, IconButton } from "@mui/material";
import "./index.css";
import HeadLine from "../../../components/HeadLine";
import { ArrowBack } from "@mui/icons-material";
import SpecimenForm from "../../../components/SpecimenForm";
import { SpecimenFormData, Specimen, Image } from "../../../types";
import ImageViewer from "./ImageViewer";
import HorizontalGallery from "./HorizontalGallery";
import { ROUTES } from "../../../config/routes";
import { useNavigate } from "react-router";

const SpecimenViewer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sendRequest } = useWebServer();
  const [selectedImageUrl, setSelectedImageUrl] = React.useState<string>("");
  const [prevSpe, setPrevSpe] = React.useState<string>("");
  const [beforeSpecimen, setBeforeSpecimen] = React.useState<Specimen>();
  const [nextSpecimen, setNextSpecimen] = React.useState<Specimen>();
  const { allSpecimens } = useAppSelector((state) => state.specimens);
  const selectedCollection = useAppSelector(
    (state) => state.collections.selectedCollection
  );
  const selectedSpecimen = useAppSelector(
    (state) => state.specimens.currentSpecimen
  );
  const collectionLoading = useAppSelector((state) => {
    const selectedId = state.collections.selectedCollection?.id || "";
    return state.specimens.allSpecimens[selectedId]?.loading || false;
  });

  const handleSpecimenSubmit = (
    specimenId: string,
    collectionId: string,
    data: SpecimenFormData
  ) => {
    dispatch(updateSpecimen({ sendRequest, specimenId, data, collectionId }));
  };

  useEffect(() => {
    if (!selectedCollection?.id) {
      navigate(ROUTES.HOME);
    }
  }, [selectedCollection, navigate]);

  useEffect(() => {
    if (prevSpe !== selectedSpecimen?.id) {
      setSelectedImageUrl(selectedSpecimen?.img || "");
      setPrevSpe(selectedSpecimen?.id || "");
    }
    const curCollection = allSpecimens[selectedCollection?.id || ""];
    const specimens = curCollection.specimens || [];
    const index = specimens.findIndex((spe) => {
      return spe.id === selectedSpecimen?.id;
    });
    if (index >= 0) {
      if (index === 0) {
        setBeforeSpecimen(undefined);
      } else {
        setBeforeSpecimen(specimens?.[index - 1]);
      }

      if (index + 1 > specimens.length) {
        setNextSpecimen(undefined);
      } else {
        setNextSpecimen(specimens?.[index + 1]);
      }
    }
    if (
      index > specimens.length - 10 &&
      specimens.length !== curCollection.count &&
      !collectionLoading
    ) {
      dispatch(
        fetchSpecimensByCollectionId({
          sendRequest,
          collectionId: selectedCollection?.id || "",
          page: Math.floor(index / 40) + 1,
          pageSize: 40,
        })
      );
    }
    // eslint-disable-next-line
  }, [selectedSpecimen, prevSpe]);

  const fetchImagesById = () => {
    dispatch(
      fetchSpecimenImagesById({
        sendRequest,
        specimenId: selectedSpecimen?.id || "",
        collectionId: selectedSpecimen?.collectionId || "",
      })
    );
  };

  const handleSelectImageToView = (img: Image) => {
    setSelectedImageUrl(img.img);
  };

  const selectPrevSpecimen = () => {
    if (beforeSpecimen) {
      dispatch(setCurrentSpecimen(beforeSpecimen));
    }
  };

  const selectNextSpecimen = () => {
    if (nextSpecimen) {
      dispatch(setCurrentSpecimen(nextSpecimen));
    }
  };

  return (
    <div className="gallery-specimens">
      <div className="btn-go-back-collections">
        <IconButton>
          <ArrowBack
            color="primary"
            onClick={() => {
              window.history.back();
            }}
          />
        </IconButton>
      </div>
      <Box className="gallery-headline">
        <HeadLine
          value={selectedSpecimen?.displayName || selectedSpecimen?.id || ""}
          handleGoBack={beforeSpecimen ? () => selectPrevSpecimen() : undefined}
          handleGoNext={nextSpecimen ? () => selectNextSpecimen() : undefined}
          showNav={true}
        />
      </Box>
      <Grid container>
        <Grid size={8}>
          <div>
            <ImageViewer imageUrl={selectedImageUrl || ""} />
          </div>
        </Grid>

        <Grid size={4}>
          <div className="place-holder-form-specimen">
            <SpecimenForm
              specimen={selectedSpecimen}
              onSubmit={handleSpecimenSubmit}
            />
          </div>
        </Grid>
        <Grid size={12}>
          <HorizontalGallery
            selectedSpecimen={selectedSpecimen}
            fetchImagesById={fetchImagesById}
            imgs={selectedSpecimen?.imgs || []}
            selectImageToView={handleSelectImageToView}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default SpecimenViewer;
